import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';

// 다국어 사전
const translations = {
  ko: {
    title: "AI 냉장고 매니저 🧊",
    select: "📸 사진 선택 (최대 5장)",
    analyze: "지금 바로 분석하기",
    analyzing: "AI가 열심히 분석 중입니다...",
    result: "🔍 발견된 재료",
    empty: "냉장고가 비어있어요. 사진을 올려주세요!",
    unknownAsk: "🤔 AI가 잘 모르는 재료가 있어요!:",
    addManual: "직접 재료 추가하기",
    recipeBtn: "이 재료들로 레시피 다시 받기",
    reminder: "⏰ 리마인더에 저장",
    lang: "English"
  },
  en: {
    title: "AI Fridge Manager 🧊",
    select: "📸 Select Photos (Max 5)",
    analyze: "Analyze Now",
    analyzing: "AI is analyzing...",
    result: "🔍 Found Ingredients",
    empty: "Your fridge is empty. Upload Photos!",
    unknownAsk: "🤔 AI is not sure about!:",
    addManual: "Add Custom Ingredient",
    recipeBtn: "Get Recipes with these items",
    reminder: "⏰ Save to Reminder",
    lang: "한국어"
  }
};

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lang, setLang] = useState('ko');
  const [isDark, setIsDark] = useState(false);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]); // 미리보기 기능
  const [loading, setLoading] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [unknowns, setUnknowns] = useState([]); // AI가 모르는 재료
  const [customInput, setCustomInput] = useState(''); // 수동 입력
  const [recipes, setRecipes] = useState([]);

  const t = translations[lang];

  useEffect(() => {
    const savedData = localStorage.getItem('fridgeAppData');
    if(savedData){
      const {ingredients, recipes} = JSON.parse(savedData);
      setIngredients(ingredients);
      setRecipes(recipes);
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    //미리보기 생성
    setPreviews(files.map(file => URL.createObjectURL(file)));
  };

  // 분석 버튼 눌렀을 때 실행되는 함수
  const handleAnalyze = async () => {
    if (images.length === 0) return alert("사진을 선택해주세요!");
    setLoading(true);

    const formData = new FormData();

    for(let i = 0; i < images.length; i++) {
      const file = images[i];
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      try{
        const compressedFile = await imageCompression(file, options);
        // 압축된 사진을 담기
        formData.append('images', compressedFile,file.name);
      } catch(error){
        console.log("압축 에러:", error);
      }
    }

    formData.append('language', lang); // 선택한 언어도 같이 서버로 전송

    try {
      const res = await fetch(`https://ai-fridge.duckdns.org/api/analyze-fridge`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      setIngredients(data.ingredients || []);
      setUnknowns(data.unknowns || []); // 모르는 재료도 받아오기!
      setRecipes(data.recipes || []);
      // 분석 완료 후 자동 저장
      localStorage.setItem('fridgeAppData', JSON.stringify({ingredients: data.ingredients, recipes: data.recipes}));
    } catch (error) {
      alert("분석 중 에러가 발생했습니다. 서버 터미널을 확인해주세요.");
    }finally{
      setLoading(false);
    }
  };

  const addCustomIngredient = () => {
    if (customInput.trim() !== '') {
      const newIngredients = ([...ingredients, customInput]);
      setIngredients(newIngredients);
      setCustomInput('');
      setUnknowns([]);
      localStorage.setItem('fridgeAppData', JSON.stringify({ingredients: newIngredients, recipes}));
    }
  };

  const themeClass = isDark
    ? "min-h-screen bg-slate-900 text-slate-100 p-4 font-sans transition-colors duration-300"
    : "min-h-screen bg-[#F8FAFC] text-slate-800 p-4 font-sans transition-colors duration-300";

  const cardClass = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100";


  // 리마인더 연동 함수
  const saveToReminder = (recipe) => {
    if (navigator.share) {
      navigator.share({
        title: '👨‍🍳 AI 냉장고 레시피',
        text: `[${recipe.name}]\n\n설명: ${recipe.desc}\n\n맛있게 요리해 보세요!`,
      }).catch(console.error);
    } else if (window.location.protocol === 'http:') {
      alert("공유 기능은 HTTPS(자물쇠) 환경에서만 작동합니다. 나중에 Nginx 설정을 하면 해결됩니다! 🔒");
    } else {
      alert("이 기기에서는 공유 기능을 지원하지 않습니다.");
    }
  };

  return (
    <div className={themeClass}>
      
      <div className={`mb-6 p-4 rounded-2xl shadow-sm border flex justify-between items-center transition-colors duration-300 ${cardClass}`}>
          <h1 className = "text-xl font-bold text-indigo-500">{t.title}</h1>

          <div className="relative">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-2xl">
              {isMenuOpen ? '✖️' : '☰'}
            </button>

             {isMenuOpen && (
              <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-xl border z-50 p-2 ${cardClass}`}>
              <button
                onClick={() => setLang(lang === 'ko' ? 'en' : 'ko')}
                className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors border ${isDark ? 'bg-slate-800 hover:bg-slate-600' : 'bg-white border-slate-200'}`}
              >
               🌐 {t.lang}
              </button>
                {/* 다크/라이트 모드 스위치 버튼 */}
              <button
                onClick={() => setIsDark(!isDark)}
                className="text-xl hover:scale-110 transition-transform"
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
            )}
          </div>
        </div>
                   
      {/* 사진 업로드 영역 */}
      <div className={`rounded-3xl p-6 shadow-md mb-6 border transition-colors duration-300 ${cardClass}`}>
        {previews.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mb-4">
          {previews.map((src, i) => (
            <img key={i} src={src} className="w-20 h-20 object-cover rounded-xl border shadow-sm" alt="preview" />
          ))}
        </div>
      )}

        <label className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer hover:border-indigo-400 transition-colors ${isDark ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
          <span className="font-medium">{t.select}</span>
          <input type="file" multiple className="hidden" onChange={handleFileChange} />
        </label>

        <button
          onClick={handleAnalyze}
          disabled={loading}
          className="w-full mt-4 bg-indigo-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors"
        >
          {loading ? t.analyzing : t.analyze}
        </button>
      </div>

      {/* 결과 화면 (재료 & 레시피) */}
      <div className="space-y-4">
        <div className={`rounded-2xl p-6 shadow-sm border transition-colors duration-300 ${cardClass}`}>
          <h2 className="text-lg font-bold mb-3">{t.result}</h2>

          {ingredients.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-4">
              {ingredients.map((item, idx) => (
                <span key={idx} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
          ) : <p className="text-sm opacity-60 mb-4">{t.empty}</p>}

          {(unknowns.length > 0 || ingredients.length > 0) && (
            <div className={`mt-4 p-4 rounded-xl transition-colors duration-300 ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
              {unknowns.length > 0 && (
                <p className="text-sm text-rose-500 mb-2 font-bold">{t.unknownAsk} {unknowns.join(', ')}</p>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="예: 간장, 양파..."
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors duration-300 ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300 text-black'}`}
                />
                <button onClick={addCustomIngredient} className="bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-600 transition-colors">
                  추가
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 레시피 목록 */}
        {recipes.map((recipe, idx) => (
          <div key={idx} className={`rounded-2xl p-6 shadow-sm border-l-4 border-indigo-500 transition-colors duration-300 ${cardClass}`}>
            <h3 className="text-lg font-bold mb-2">{recipe.name}</h3>
            <p className="text-sm opacity-80 mt-2 mb-4">{recipe.desc}</p>
            <button
              onClick={() => saveToReminder(recipe)}
              className={`w-full py-2 rounded-lg font-semibold text-sm transition-colors duration-300 ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'}`}
            >
              {t.reminder}
            </button>
          </div>
        ))}
      </div>

    </div>
  );
}

export default App;